import { matchKeys } from '@/hooks/matches/matchKeys';
import { gameSequenceKeys } from '@/hooks/tie-breaker/gameSequenceKeys';
import { rotationsInfoKeys } from '@/hooks/tie-breaker/rotationsInfoKeys';
import {
	createGameSequence,
	CreateGameSequencesPayload,
	CreateRotationPayload,
	createTieBreakerRotation
} from '@/services/actions/tie-breaker/rotations';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

export const useTieBreakerMutation = (matchupId: string) => {
	const queryClient = useQueryClient();

	const createRotation = useMutation({
		mutationFn: async (data: CreateRotationPayload) => {
			return await createTieBreakerRotation(data, matchupId);
		},
		onSuccess: () => {
			toast.success('Tiebreaker lineup saved successfully.');
			queryClient.invalidateQueries({
				queryKey: rotationsInfoKeys.rotationsInfo(matchupId)
			});
		},
		onError: () => {
			toast.error('Failed to create tiebreaker lineup. Please try again.');
		}
	});

	return {
		createRotation,
		isCreating: createRotation.isPending
	};
};

export const useGameSequencesMutation = (
	sequenceMatchcupId: string,
	gameNumber: string,

	matchcupId?: string
) => {
	const queryClient = useQueryClient();

	const invalidateQueries = async () => {
		await Promise.all([
			queryClient.invalidateQueries({ queryKey: gameSequenceKeys.gameSequenceInfo(sequenceMatchcupId) }),
			queryClient.invalidateQueries({ queryKey: rotationsInfoKeys.lastSequenceInfo(sequenceMatchcupId, Number(gameNumber)) }),
			queryClient.refetchQueries({ queryKey: matchKeys.matches(matchcupId || '') })
		]);
	};

	const createSequence = useMutation({
		mutationFn: async (data: CreateGameSequencesPayload) => {
			return await createGameSequence(data, sequenceMatchcupId, gameNumber);
		},
		onSuccess: async () => {
			await invalidateQueries();
			toast.success('Changes saved.');
		},
		onError: () => {
			toast.error('Failed to create next game. Please try again.');
		}
	});

	return {
		createSequence,
		isCreatingSequence: createSequence.isPending,
		invalidateQueries
	};
};
